import { Fragment, ReactElement, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid } from '@breathelife/mui';
import { Input } from '@breathelife/ui-components';
import { CountEqualConditionValue } from '@zinnia/form-engine-sdk';

import { NumberInput } from '../../../Components/NumberInput/NumberInput';
import {
  getConditionValidationError,
  ValidationValues,
} from '../../../Helpers/inputValidation/form/salesDecisionRules';
import { CollectionOperators } from '../Helpers/CollectionOperators';
import { ConditionTargetNodeAutocomplete } from '../Helpers/ConditionTargetNodeAutocomplete';
import { NumberComparisonConditionOperatorSelect } from '../Helpers/NumberComparisonConditionOperatorSelect';
import { ValidationErrorText } from '../ValidationErrorText';
import { ConditionCriteriaProps } from '../types';

export function CountConditionCriteria({
  condition,
  nodeIdList,
  nodeIdInCollectionMap,
  collectionContext,
  onConditionPropertiesChange,
  language,
  conditionIndex,
}: ConditionCriteriaProps<CountEqualConditionValue>): ReactElement {
  const { t } = useTranslation();
  const validationErrors = getConditionValidationError(condition, nodeIdList, nodeIdInCollectionMap);

  const [value, setValue] = useState<number | undefined>(condition.value);
  const [controlValue, setControlValue] = useState(condition.controlValue);

  const onControlValueBlur = useCallback(() => {
    onConditionPropertiesChange({ controlValue });
  }, [onConditionPropertiesChange, controlValue]);

  const onValueBlur = useCallback(() => {
    onConditionPropertiesChange({ value });
  }, [onConditionPropertiesChange, value]);

  const collectionNodeIds = nodeIdInCollectionMap[condition.targetNodeId] ?? [];

  return (
    <Fragment>
      <Grid item xs={12}>
        <ConditionTargetNodeAutocomplete
          value={condition.targetNodeId}
          nodeIdList={nodeIdList}
          onSelect={(targetNodeId) => {
            if (targetNodeId !== null) {
              onConditionPropertiesChange({ targetNodeId });
            }
          }}
          validationError={validationErrors?.[ValidationValues.targetNodeId]}
          selectedLanguage={language}
        />
      </Grid>
      <Grid item xs={12}>
        <Input
          inputVariant='outlined'
          label={t('admin.conditions.labels.answer')}
          value={controlValue}
          error={Boolean(validationErrors?.[ValidationValues.controlValue])}
          onChange={(event) => setControlValue(event.target.value)}
          onBlur={onControlValueBlur}
        />
        {validationErrors?.[ValidationValues.controlValue]?.message && (
          <ValidationErrorText>{validationErrors[ValidationValues.controlValue].message}</ValidationErrorText>
        )}
      </Grid>
      <Grid item xs={12}>
        <NumberComparisonConditionOperatorSelect
          value={condition.operator}
          onChange={(operator) => {
            onConditionPropertiesChange({ operator });
          }}
        />
        {validationErrors?.[ValidationValues.operator]?.message && (
          <ValidationErrorText>{validationErrors[ValidationValues.operator].message}</ValidationErrorText>
        )}
      </Grid>
      <Grid item xs={12}>
        <NumberInput
          inputVariant='outlined'
          value={value}
          onNumberChange={setValue}
          onBlur={onValueBlur}
          label={t('admin.conditions.labels.controlValue')}
          error={Boolean(validationErrors?.[ValidationValues.value])}
        />
        {validationErrors?.[ValidationValues.value]?.message && (
          <ValidationErrorText>{validationErrors[ValidationValues.value].message}</ValidationErrorText>
        )}
      </Grid>
      <CollectionOperators
        collectionContext={collectionContext}
        collectionNodeIds={collectionNodeIds}
        collectionOperators={condition.collectionOperators}
        onConditionPropertiesChange={onConditionPropertiesChange}
        conditionIndex={conditionIndex}
      />
    </Fragment>
  );
}
